import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import storage from "@/src/base/storage";
import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES, type AllowedImageType } from "../forms/template-schema";

/**
 * Generate storage path for a PDF report
 * @param licenciadaId
 * @param empresaId
 * @param questionarioId
 * @param templateId
 */
export function generateReportStoragePath(
  licenciadaId: string,
  empresaId: string,
  questionarioId: string,
  templateId: string
): string {
  const timestamp = Date.now();
  return `licenciadas/${licenciadaId}/relatorios/${empresaId}/${questionarioId}/${timestamp}_${templateId}.pdf`;
}

/**
 * Upload a PDF blob to Firebase Storage
 * @param blob - The PDF blob
 * @param path - The storage path
 * @returns - The download URL
 */
export async function uploadPDF(blob: Blob, path: string): Promise<string> {
  const fileReference = ref(storage, path);

  const metadata = {
    contentType: "application/pdf"
  };

  await uploadBytes(fileReference, blob, metadata);
  return await getDownloadURL(fileReference);
}

/**
 * Get download URL for a storage path
 * @param path
 */
export async function getReportDownloadUrl(path: string): Promise<string> {
  const fileReference = ref(storage, path);
  return getDownloadURL(fileReference);
}

/**
 * Delete a PDF from storage
 * @param path
 */
export async function deletePDF(path: string): Promise<void> {
  const fileReference = ref(storage, path);
  await deleteObject(fileReference);
}

/**
 * Generate a filename for the downloaded PDF
 * @param empresaNome
 * @param questionarioNome
 * @param dataGeracao
 */
export function generateReportFilename(empresaNome: string, questionarioNome: string, dataGeracao: Date): string {
  const sanitize = (string_: string) =>
    string_
      .normalize("NFD")
      .replaceAll(/[\u0300-\u036F]/g, "")
      .replaceAll(/[^\d\sA-Za-z-]/g, "")
      .replaceAll(/\s+/g, "_")
      .slice(0, 50);

  const empresaSafe = sanitize(empresaNome);
  const questionarioSafe = sanitize(questionarioNome);
  const dateString = dataGeracao.toISOString().split("T")[0];

  return `Relatorio_${empresaSafe}_${questionarioSafe}_${dateString}.pdf`;
}

/**
 * Download a blob as a file
 * @param blob
 * @param filename
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Template Image Storage Functions
// ============================================================================

/**
 * Result of image validation
 */
export interface ImageValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate image file type and size
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(file: File): ImageValidationResult {
  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as AllowedImageType)) {
    return {
      valid: false,
      error: `Tipo de arquivo não permitido. Use: JPG, PNG ou WebP`
    };
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    const maxSizeMB = MAX_IMAGE_SIZE_BYTES / (1024 * 1024);
    return {
      valid: false,
      error: `Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`
    };
  }

  return { valid: true };
}

/**
 * Get file extension from MIME type
 * @param mimeType - The MIME type (e.g., "image/jpeg")
 * @returns File extension (e.g., "jpg")
 */
function getExtensionFromMimeType(mimeType: string): string {
  const mimeToExtension: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp"
  };
  return mimeToExtension[mimeType] || "jpg";
}

/**
 * Generate storage path for a template background image
 * @param licenciadaId - The licenciada ID
 * @param templateId - The template ID
 * @param imageId - Unique image identifier
 * @param mimeType - The image MIME type
 * @returns Storage path string
 */
export function generateImageStoragePath(
  licenciadaId: string,
  templateId: string,
  imageId: string,
  mimeType: string
): string {
  const extension = getExtensionFromMimeType(mimeType);
  return `licenciadas/${licenciadaId}/templates/${templateId}/backgrounds/${imageId}.${extension}`;
}

/**
 * Upload a template background image to Firebase Storage
 * @param file - The image file
 * @param licenciadaId - The licenciada ID
 * @param templateId - The template ID
 * @param imageId - Unique image identifier
 * @returns Object with download URL and storage path
 * @throws Error if validation fails or upload fails
 */
export async function uploadTemplateImage(
  file: File,
  licenciadaId: string,
  templateId: string,
  imageId: string
): Promise<{ url: string; storagePath: string }> {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Generate storage path
  const storagePath = generateImageStoragePath(licenciadaId, templateId, imageId, file.type);

  // Upload to Firebase Storage
  const fileReference = ref(storage, storagePath);
  const metadata = {
    contentType: file.type,
    customMetadata: {
      templateId,
      imageId,
      originalName: file.name
    }
  };

  await uploadBytes(fileReference, file, metadata);
  const url = await getDownloadURL(fileReference);

  return { url, storagePath };
}

/**
 * Delete a template background image from Firebase Storage
 * @param storagePath - The storage path of the image to delete
 */
export async function deleteTemplateImage(storagePath: string): Promise<void> {
  const fileReference = ref(storage, storagePath);
  await deleteObject(fileReference);
}
